(ns minorodata.components.admin.forms
  (:require
   ["copy-to-clipboard" :as copy]
   ["lucide-react" :refer [ChevronUp CopyIcon Info Save Trash2]]
   [applied-science.js-interop :as jsi]
   [cljs.spec.alpha :as spec]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.components.app-admin.forms :as forms]
   [minorodata.components.spinner.core :as spinner.core]
   [mold-forms.core :as form-component]
   [mold-forms.model :refer [->form-model]]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [spec-tools.core :as st]
   [ui.alert :as alert]
   [ui.button :refer [button]]
   [ui.card :refer [card card-content card-title]]
   [ui.fields :refer [checkbox input select]]
   [ui.table :refer [table tbody td th thead tr]]
   [ui.tooltip :refer [tooltip]]
   [ui.typography :as typography]))


(spec/def ::clientname
  (spec/and (st/spec string? {:reason "Client name should be a string"})
            (st/spec (complement str/blank?) {:reason "Client name can not be empty"})))


(spec/def ::slack-webhook
  (st/spec string? {:reason "Slack webhook should be a string"}))

(spec/def ::ms-teams-webhook
  (st/spec string? {:reason "MS Teams webhook should be a string"}))


(spec/def ::slack-webhook-form
  (spec/keys :req-un [::slack-webhook]))

(spec/def ::teams-webhook-form
  (spec/keys :req-un [::ms-teams-webhook]))


(spec/def ::cient-form
  (spec/keys :req-un [::clientname]))


(spec/def ::user-name
  (spec/and (st/spec string? {:reason "Username should be a string"})
            (st/spec (complement str/blank?) {:reason "Username can not be empty"})))


(spec/def ::login-name
  (spec/and (st/spec string? {:reason "Login should be a string"})
            (st/spec (complement str/blank?) {:reason "Login can not be empty"})
            (st/spec #(re-matches #"^\S+@\S+\.\S+$" %) {:reason "Login should be an email"})))

(spec/def ::role
  (spec/and (st/spec string? {:reason "Role should be a string"})
            (st/spec (complement str/blank?) {:reason "Role can not be empty"})))


;; As per NIST recomendations, only length should be limited. There should not
;; be any limitations on additional password complexity as it doesn't improve
;; security.
;; https://auth0.com/blog/dont-pass-on-the-new-nist-password-guidelines/

(spec/def ::password
  (spec/and (st/spec string? {:reason "Password should be a string"})
            (st/spec (complement str/blank?) {:reason "Password can not be empty"})
            (st/spec #(<= 12 (count %)) {:reason "Password should be at least 12 characters long"})))


(spec/def ::new-user-form
  (spec/keys :req-un [::user-name ::login-name ::role ::password]))


(spec/def ::user-form
  (spec/keys :req-un [::user-name ::login-name ::role]))


(spec/def ::password-form
  (spec/keys :req-un [::password]))


(defn submit-new-client-form [form-state]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:users.api/create-client-data form-data])))


(defn submit-client-form [form-state]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:users.api/update-client-data form-data])
    (dispatch [:users.api/im-v2-update-client-data form-data])))


(defn submit-new-user-form [form-state on-close]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:users.api/create-client-user {:params form-data :on-close on-close}])))


(defn submit-user-form [form-state]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:users.api/update-client-user form-data])
    (dispatch [:users.api/im-v2-update-client-user form-data])))


(defn submit-user-password-form [form-state on-close]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:users.api/update-client-user-password {:params form-data :on-close on-close}])
    (dispatch [:users.api/im-v2-update-client-user-password {:params form-data :on-close on-close}])))


(defn submit-delete-user [user-id]
  (dispatch [:users.api/delete-client-user {:id user-id}])
  (dispatch [:users.api/im-v2-delete-client-user user-id]))


(defn submit-notification-preferences-form [form-state]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:users.api/bulk-update-notification-preferences form-data])))

(defn- conj-permissions
  [acc permission keyname]
  (cond-> acc
    (#{"read" "read+write"} keyname) (conj (str permission "/read"))
    (#{"write" "read+write"} keyname) (conj (str permission "/write"))))

(defn role-form->permissions
  [form-data]
  (reduce (fn [acc [field-key checked]]
            (let [[permission keyname] (-> field-key name (str/split #"\."))]
              (cond-> acc
                checked (conj-permissions permission keyname))))
          []
          (dissoc form-data :name)))

(defn parse-role-form-state
  [form-state]
  (let [form-data   (form-component/model->fields form-state)
        role-name   (:name form-data)
        permissions (role-form->permissions form-data)]
    {:name        role-name
     :permissions permissions}))

(defn submit-new-role-form
  [form-state]
  (let [params (parse-role-form-state form-state)]
    (dispatch [:users.api/create-role params])))

(defn submit-edit-role-form
  [id form-state]
  (let [params (parse-role-form-state form-state)]
    (dispatch [:users.api/update-role (assoc params :id id)])))

(defn timezone->option [{:keys [name abbrev]}]
  {:value name
   :text  (str name (when (not= name abbrev) (str " (" abbrev ")")))})

(defn roles->options
  [roles]
  (mapv (fn [{:role/keys [id name]}] {:value id :text name})
        roles))

(defn client-form-render
  [{:keys [on-submit errors clientname cid timezone]}]
  (let [timezones @(re-posh/subscribe [:timezones/prop :timezones/data])
        options   (map timezone->option timezones)]
    [:form {:on-submit on-submit}
     [:div {:class "my-4 flex gap-4"}
      [input {:field  clientname
              :errors errors
              :id     :clientname
              :label  "Client name"}]

      [select {:field   timezone
               :errors  errors
               :id      :timezone
               :label   "Timezone"
               :options options}]]

     [:div {:class "text-kleene-text-secondary *:text-xs"}
      [typography/p
       "All schedules will be triggered according to the time zone selected."]
      [typography/p
       "We'll automatically update the time if your time zone uses DST (Daylight Saving Time)."]]

     [:div {:class "flex justify-end mt-4"}
      [button {:type "submit"}
       "Save"]]
     (when cid
       [:div {:class "flex justify-end items-center gap-2 mt-2 *:text-xs text-kleene-text-secondary"}
        [typography/p "Client id: " ]
        [button {:variant    :empty
                 :size       :micro
                 :title      "Copy client ID to clipboard"
                 :on-click   (fn []
                               (copy cid #js {:format "text/plain"})
                               (dispatch [:app.events/add-snack :success "Client ID copied to clipboard"]))
                 :icon-right CopyIcon}
         cid]])]))

(defn new-user-account-form-render [{:keys [on-submit errors user-name login-name role password roles]}]
  (let [loading?             @(re-posh/subscribe [:mold-http/loading? :users.api/create-client-user])
        client-roles-options (roles->options roles)]
    [:form {:class     "flex flex-col gap-2"
            :on-submit on-submit}
     [input
      {:field      user-name
       :errors     errors
       :id         :username
       :variant    "standard"
       :auto-focus true
       :label      "Username"}]

     [input
      {:field         login-name
       :errors        errors
       :id            :login-name
       :variant       "standard"
       :label         "Login (email)"
       :auto-complete "email"}]

     [select
      {:field   role
       :errors  errors
       :variant "standard"
       :id      :role
       :label   "Role"
       :native  false
       :options client-roles-options}]

     [input
      {:field         password
       :errors        errors
       :id            :password
       :variant       "standard"
       :label         "Password"
       :auto-complete "off"
       :type          :password}]

     (forms/new-pwd-indicator password)

     [:div {:class "flex justify-end mt-4"}
      [button {:type     "submit"
               :disabled loading?}
       [:span
        "Create user"
        (when loading?
          [spinner.core/spinner-lucide {:size 24 :class "absolute left-1/2 top-1/2 -ml-3 -mt-3"}])]]]]))


(defn change-password-user-account-form-render [{:keys [on-submit errors password]}]
  [:form {:on-submit on-submit}
   [input
    {:field   password
     :errors  errors
     :id      :password
     :variant "standard"
     :label   "New password"
     :type    :password}]

   [forms/new-pwd-indicator password]

   [:div {:class "flex justify-end"}
    [button {:type  "submit"
             :class "mt-4"}
     "Save password"]]])

(defn edit-user-account-form-render [{:keys [on-submit errors user-name login-name role roles]}]
  (let [client-roles-options (roles->options roles)]
    [:form {:class     "flex flex-col gap-2"
            :on-submit on-submit}
     [input
      {:field      user-name
       :errors     errors
       :id         :username
       :variant    "standard"
       :auto-focus true
       :label      "Name"}]

     [input
      {:field         login-name
       :errors        errors
       :id            :login-name
       :variant       "standard"
       :label         "Username"
       :auto-complete "email"}]

     [select
      {:field   role
       :errors  errors
       :id      :role
       :label   "Role"
       :variant "standard"
       :type    :role
       :native  false
       :options client-roles-options}]

     [:div {:class "text-right"}
      [button {:type "submit"}
       "Save"]]]))

(def combined-permissions #{:connections+transforms :new-source :sources :transforms :file-ingest :webforms :sql-browser})

(def permission-label
  {:read+write "View and Edit"
   :read       "View"
   :write      "Edit"})

(def permission-sections-old
  [[:connections+transforms [:span "Connections" [:br] "Transforms"] [:read+write]]
   [:file-ingest "File Ingest" [:read+write]]
   [:webforms "Webforms" [:read+write]]
   [:sql-browser "SQL Console" [:read+write]]
   [:dag "DAG" [:read]]
   [:logs "Logs" [:read]]
   [:metrics "Metrics" [:read]]
   [:dynamic-docs "Dynamic Docs" [:read :write]]
   [:warehouse-health "Snowflake Costs" [:read]]
   [:data-unit-tests-results "Unit Test Results" [:read]]
   [:dashboards "Dashboards" [:read] "fe.dashboards"]
   [:data-views "Data Views" [:read :write]]
   [:tables "Tables" [:read]]
   [:erd "ERD" [:read] "fe.erd"]
   [:summary "Summary" [:read]]])

(defn role-permissions-row-old
  [{[k label permission-keys feature-flag] :section
    :keys                                  [fields errors]}]
  (when (or (not feature-flag)
            @(re-posh/subscribe [:unleash/is-enabled feature-flag]))
    [:div {:class "grid grid-cols-7 gap-1 items-center h-10"}
     [:h4 {:class "m-0 col-span-3 font-normal"} label]
     (for [permission permission-keys]
       (let [full-name (str (name k) "." (name permission))]
         [:div {:key   full-name
                :class (if (= permission :read+write) "col-span-4" "col-span-2")}
          [checkbox
           {:field       (get fields (keyword full-name))
            :errors      errors
            :id          full-name
            :type        "checkbox"
            :variant     "standard"
            :margin      "none"
            :color       "primary"
            :size        "small"
            :label-class "mr-0"
            :label       (reagent/as-element
                          [:span {:class "text-sm"} (get permission-label permission)])}]]))]))

(defn edit-role-form-render-old [{:keys [on-submit fields errors show-warning] :as props}]
  [:form {:on-submit on-submit}
   [input
    {:field      (:name props)
     :errors     errors
     :id         :name
     :variant    "standard"
     :auto-focus true
     :label      "Role name"}]

   [:div {:class "my-4"}
    [typography/h3 "Permissions"]

    [:div {:class "flex flex-col my-2"}
     (for [section permission-sections-old]
       [role-permissions-row-old {:key     (first section)
                                  :section section
                                  :errors  errors
                                  :fields  fields}])]]
   (when show-warning
     [typography/p {:class "text-kleene-text-error"} "Any changes are only made active after log out and log in"])
   [:div {:class "mt-2 text-right"}
    [button {:type "submit"}
     "Save"]]])

(def permission-sections
  [{:title       "Ingest"
    :description "Set up and manage connectors"
    :items
    [{:title       "Add new source"
      :description "Create and delete new connections"
      :key         :new-source
      :permissions #{:read+write}}
     {:title       "Sources"
      :description "Manage APIs and DB connectors"
      :key         :sources
      :permissions #{:read+write}}
     {:title       "File ingest"
      :description "Upload CSVs"
      :key         :file-ingest
      :permissions #{:read+write}}
     {:title       "Webforms"
      :description "Write to the DB tables"
      :key         :webforms
      :permissions #{:read+write}}]}

   {:title       "Model"
    :description "Transform the data"
    :items
    [{:title        "Data models"
      :description  "Entity relationships between tables"
      :key          :erd
      :permissions  #{:read}
      :feature-flag "fe.erd"}
     {:title       "Tables"
      :description "See information about tables in the warehouse"
      :key         :tables
      :permissions #{:read}}
     {:title       [:span {:class "flex items-center gap-2"}
                    "Transforms"
                    [tooltip {:content "Editing of Transforms and the SQL Console is currently a combined permission"}
                     [:> Info {:size 16}]]]
      :description "Use SQL to query the data"
      :key         :transforms
      :permissions #{:read+write}}
     {:title       "Pipelines"
      :description "See a visualisation of the transform steps"
      :key         :dag
      :permissions #{:read}}
     {:title       [:span {:class "flex items-center gap-2"}
                    "SQL Console"
                    [tooltip {:content "Editing of Transforms and the SQL Console is currently a combined permission"}
                     [:> Info {:size 16}]]]
      :description "Write and edit SQL transforms"
      :key         :sql-browser
      :permissions #{:read+write}}
     {:title       "Data docs"
      :description "Records of the current transforms"
      :key         :dynamic-docs
      :permissions #{:read :write}}
     {:title       "Pipeline editor"
      :description "Edit transforms in a sandboxed environment"
      :key         :pipeline-editor
      :permissions #{:read :write}}
     {:title        "Analytics models"
      :description  "Run and manage analytics models"
      :key          :analytics-models
      :permissions  #{:read :write}
      :feature-flag "fe.analytics-models"}]}

   {:title       "Insights"
    :description "Business performance in visual form"
    :items
    [{:title       "Share data"
      :description "See and download data sets"
      :key         :data-views
      :permissions #{:read :write}}
     {:title        "Data apps"
      :description  "A suite of visualisations and dashboards"
      :key          :dashboards
      :permissions  #{:read}
      :feature-flag "fe.dashboards"}]}

   {:title       "Manage"
    :description "Monitor the health of the system"
    :items
    [{:title       "Logs"
      :description "Granular accounts of system events"
      :key         :logs
      :permissions #{:read}}
     {:title       "Data quality"
      :description "Data unit test results"
      :key         :data-unit-tests-results
      :permissions #{:read}}
     {:title       "Warehouse use"
      :description "Monitor costs for the warehouse"
      :key         :warehouse-health
      :permissions #{:read}}]}])

(defn role-permissions-row
  [{:keys [item fields errors]}]
  (let [{:keys [key title description permissions feature-flag]} item]
    (when (or (not feature-flag)
              @(re-posh/subscribe [:unleash/is-enabled feature-flag]))
      [tr {:class "py-2"}
       [td]
       [td {:class "m-0 font-normal"}
        [typography/h4 {} title]
        [typography/p {:class "text-kleene-text-secondary text-xs"} description]]
       [td {:class "text-center"}
        (let [permission (or (permissions :read)
                             (permissions :read+write))
              full-name  (keyword (str (name key) "." (name permission)))]
          [checkbox
           {:field       (get fields full-name)
            :errors      errors
            :disabled    (= :read+write permission)
            :id          (str (name key) "." "read")
            :size        "small"
            :label-class "mr-0"
            :class       "mx-auto"}])]
       [td {:class "text-center"}
        (when-let [permission (or (permissions :write)
                                  (permissions :read+write))]
          (let [full-name (keyword (str (name key) "." (name permission)))]
            [checkbox
             {:field       (get fields full-name)
              :errors      errors
              :id          (str (name key) "." "write")
              :size        "small"
              :label-class "mr-0"
              :class       "mx-auto"}]))]
       [td]])))

(defn role-permission-section []
  (let [opened? (reagent/atom true)]
    (fn [{:keys [section errors fields]}]
      (let [{:keys [title description items]} section]
        [:<>
         [tr {:on-click #(swap! opened? not)
              :class    "cursor-pointer hover:*:bg-kleene-hover-background"}
          [td {:col-span 4}
           [typography/h4 title]
           [typography/p {:class "text-kleene-text-secondary text-xs"} description]]
          [td
           [:div {:class "flex items-center"}
            [:> ChevronUp {:class (when @opened? "transform rotate-180 transition-transform")}]]]]

         (when @opened?
           (for [item items]
             ^{:key (:key item)}
             [role-permissions-row {:item   item
                                    :errors errors
                                    :fields fields}]))]))))

(defn edit-role-form-render [{:keys [on-submit fields errors show-warning] :as props}]
  [:form {:on-submit on-submit}
   [input
    {:field      (:name props)
     :errors     errors
     :id         :name
     :variant    "standard"
     :auto-focus true
     :label      "Role name"}]

   [:div {:class "my-4"}
    [typography/h3 "Permissions"]
    
    (when show-warning
      [alert/alert
       [alert/alert-title "Warning"]
       [typography/p {:class "text-xs"}
        "Any changes are only made active after log out and log in"]])

    [table {:class "my-2"}
     [thead
      [tr
       [th {:col-span 2} "Area"]
       [th {:class "text-center" :style {:width 64}} "View"]
       [th {:class "text-center" :style {:width 64}} "Edit"]
       [th {:style {:width 48}}]]]
     [tbody
      (for [section permission-sections]
        ^{:key (:title section)}
        [role-permission-section {:section section
                                  :fields  fields
                                  :errors  errors}])]]]
   [:div {:class "mt-2 text-right"}
    [button {:type "submit"}
     "Save"]]])


(defn account-form [{:keys [admin-data]}]
  [:div
   [form-component/form
    (->form-model
     {:id        :client-form
      :on-submit submit-client-form
      :validate  (partial form-component/validate-form ::client-form)
      :fields    [{:name  :clientname
                   :value (:client-data/clientname admin-data)}
                  {:name  :timezone
                   :value (:client-data/timezone admin-data)}]})
    (fn [fields errors on-submit]
      [client-form-render
       {:on-submit  on-submit
        :errors     errors
        :fields     fields
        :cid        (:client-data/cid admin-data)
        :clientname (:clientname fields)
        :timezone   (:timezone fields)}])]])


(defn new-client-form []
  [:div
   [form-component/form
    (->form-model
     {:id        :client-form
      :on-submit submit-new-client-form
      :validate  (partial form-component/validate-form ::client-form)
      :fields    [{:name          :clientname
                   :default-value ""
                   :value         ""}
                  {:name  :timezone
                   :value "UTC"}]})
    (fn [fields errors on-submit]
      [client-form-render
       {:on-submit  on-submit
        :errors     errors
        :fields     fields
        :clientname (:clientname fields)}])]])


(defn notification-preferences-slack-form-render
  [{:keys [on-submit errors fields list-loading? action-loading?]}]
  (let [{:keys [slack-webhook
                slack-transforms-success slack-transforms-failure
                slack-extracts-success slack-extracts-failure]}
        fields]
    [:form {:class     "flex flex-col gap-4"
            :on-submit on-submit}
     [:header
      [typography/h3 "Slack"]
      [typography/p {:class "text-kleene-text-secondary"}
       "Set up a Slack Webhook to receive notifications directly in Slack. For more details on how Slack Webhooks work refer to "
       [:a {:href   "https://api.slack.com/messaging/webhooks"
            :target :_blank
            :class  "underline"}
        "Slack documentation"]
       "."]]

     [:div
      [input
       {:field        slack-webhook
        :errors       errors
        :id           :slack-webhook
        :variant      "standard"
        :label        "Slack Webhook URL"
        :auto-comlete "slack-webhook"}]]

     [:div {:class "pt-2"}
      [table {:stickyHeader true}

       [thead
        [tr
         [th {:width "40%"}
          "Event source"]

         [th "Success"]
         [th "Failure"]]]

       [tbody
        [tr
         [td {:width "40%"}
          "Transforms"]

         [td
          [checkbox
           {:field   slack-transforms-success
            :id      :slack-transforms-success
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]
         [td
          [checkbox
           {:field   slack-transforms-failure
            :id      :transforms-failure
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]]

        [tr
         [td {:width "40%"}
          "Extracts"]

         [td
          [checkbox
           {:field   slack-extracts-success
            :id      :extracts-success
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]

         [td
          [checkbox
           {:field   slack-extracts-failure
            :id      :extracts-failure
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]]]]]

     [:footer {:class "flex justify-end"}
      [button {:type     "submit"
               :disabled (or list-loading? action-loading?)}
       "Save"]]]))


(defn notification-preferences-teams-form-render
  [{:keys [on-submit errors fields list-loading? action-loading?]}]
  (let [{:keys [ms-teams-webhook
                ms-teams-webhook-workflows
                ms-teams-transforms-success ms-teams-transforms-failure
                ms-teams-extracts-success ms-teams-extracts-failure]}
        fields]
    [:form {:on-submit on-submit
            :class     "flex flex-col gap-4"}
     [:header
      [typography/h3 "Microsoft Teams"]
      [typography/p {:class "text-kleene-text-secondary"}
       (if (:value ms-teams-webhook-workflows)
         [:<>
          "Use Workflows for Microsoft Teams to receive notifications directly in Teams.
           For more details on how Teams Workflows work refer to "
          [:a {:href   "https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook"
               :target :_blank
               :class  "underline"}
           "Teams documentation"]]

         [:<>
          "Old Teams Webhooks "
          [:a {:href   "https://devblogs.microsoft.com/microsoft365dev/retirement-of-office-365-connectors-within-microsoft-teams/"
               :target :_blank
               :class  "underline"}
           "are deprecated"] ". "
          "Set up a Teams Webhook to receive notifications directly in Teams.
           For more details on how Teams Webhooks work refer to "
          [:a {:href   "https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook"
               :target :_blank
               :class  "underline"}
           "Teams documentation"]])]]

     [checkbox
      {:field   ms-teams-webhook-workflows
       :errors  errors
       :id      :ms-teams-webhook-workflows
       :type    "checkbox"
       :variant "standard"
       :color   "primary"
       :size    "small"
       :label   (reagent/as-element
                 [:span {:class "text-sm"}
                  "Use Workflows for Microsoft Teams"])}]

     [input
      {:field        ms-teams-webhook
       :errors       errors
       :id           :ms-teams-webhook
       :label        "Teams Webhook URL"
       :auto-comlete "ms-teams-webhook"}]

     [:div {:class "pt-2"}
      [table {:stickyHeader true}

       [thead
        [tr
         [th {:width "40%"}
          "Event source"]

         [th "Success"]
         [th "Failure"]]]

       [tbody
        [tr
         [td {:width "40%"}
          "Transforms"]

         [td
          [checkbox
           {:field   ms-teams-transforms-success
            :id      :slack-transforms-success
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]
         [td
          [checkbox
           {:field   ms-teams-transforms-failure
            :id      :transforms-failure
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]]

        [tr
         [td {:width "40%"}
          "Extracts"]

         [td
          [checkbox
           {:field   ms-teams-extracts-success
            :id      :extracts-success
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]

         [td
          [checkbox
           {:field   ms-teams-extracts-failure
            :id      :extracts-failure
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]]]]]


     [:footer {:class "flex justify-end mt-4"}
      [button {:type     "submit"
               :disabled (or list-loading? action-loading?)}
       "Save"]]]))


(defn notification-preferences-generic-form-render
  [{:keys [on-submit errors fields list-loading? action-loading?]}]
  (let [{:keys [generic-webhook
                generic-transforms-success generic-transforms-failure
                generic-extracts-failure generic-extracts-success]}
        fields]
    [:form {:class     "flex flex-col gap-4"
            :on-submit on-submit}
     [:header
      [typography/h3 "Generic webhook"]
      [typography/p {:class "text-kleene-text-secondary"}
       "Set up your generic webhook notification preferences here. We’ll send a POST request to the provided URL with the following:"
       [:br]
       [:code "message"] " – 'Event description',"
       [:br]
       [:code "status"] " – 'success' or 'fail' keyword,"
       [:br]
       [:code "job_type"] " – 'TRANSFORM' or 'EXTRACT' keyword,"
       [:br]
       [:code "job_id"] " – The unique identifier of the 'transform-id' or 'extract-id'."]]

     [:div
      [input
       {:field   generic-webhook
        :errors  errors
        :id      :generic-webhook
        :variant "standard"
        :label   "Webhook URL"}]]

     [:div {:class "pt-2"}
      [table {:stickyHeader true}

       [thead
        [tr
         [th {:width "40%"}
          "Event source"]

         [th "Success"]
         [th "Failure"]]]

       [tbody
        [tr
         [td {:width "40%"}
          "Transforms"]

         [td
          [checkbox
           {:field   generic-transforms-success
            :id      :generic-transforms-success
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]
         [td
          [checkbox
           {:field   generic-transforms-failure
            :id      :generic-transforms-failure
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]]

        [tr
         [td {:width "40%"}
          "Extracts"]

         [td
          [checkbox
           {:field   generic-extracts-success
            :id      :generic-extracts-success
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]

         [td
          [checkbox
           {:field   generic-extracts-failure
            :id      :generic-extracts-failure
            :type    "checkbox"
            :variant "standard"
            :color   "primary"}]]]]]]

     [:footer {:class "flex justify-end"}
      [button {:type     "submit"
               :disabled (or list-loading? action-loading?)}
       "Save"]]]))


(defn notification-preferences-form []
  (reagent/create-class
   {:display-name "notification-preferences"

    :constructor
    (fn []
      (dispatch [:users.api/fetch-notification-preferences]))

    :render
    (fn []
      (let [notification-preferences (re-posh/subscribe [:notification-preferences/pull])

            list-loading?            (re-posh/subscribe [:mold-http/loading? :users.api/fetch-notification-preferences])

            create-loading?          (re-posh/subscribe [:mold-http/loading? :users.api/create-notification-preference])
            update-loading?          (re-posh/subscribe [:mold-http/loading? :users.api/update-notification-preference])
            delete-loading?          (re-posh/subscribe [:mold-http/loading? :users.api/delete-notification-preference])

            {:notification-preferences/keys
             [preferences

              slack-webhook
              slack-transforms-success slack-transforms-failure
              slack-extracts-success slack-extracts-failure

              ms-teams-webhook
              ms-teams-webhook-workflows
              ms-teams-transforms-success ms-teams-transforms-failure
              ms-teams-extracts-success ms-teams-extracts-failure

              generic-webhook
              generic-transforms-success generic-transforms-failure
              generic-extracts-failure generic-extracts-success]}
            @notification-preferences

            action-loading?          (or @create-loading? @update-loading? @delete-loading?)]

        ^{:key (str preferences)}
        [:div {:class "flex flex-col gap-8 max-w-3xl"}
         [:header
          [:div {:class "flex gap-2 items-center"}
           [card-title "Notification preferences"]
           [tooltip {:content "If you will delete the general Webhook URL, all connected notifications preferences for individual jobs will be deleted as well."}
            [:> Info {:size 20}]]]
          [typography/p "Configure which types of notifications you will receive."]]

         [form-component/form
          (->form-model
           {:id        :notification-preferences-slack-form
            :on-submit submit-notification-preferences-form
            ;; :validate (partial form-component/validate-form ::slack-webhook-form)
            :fields    [{:name  :slack-webhook
                         :value (or slack-webhook "")}
                        {:name    :slack-transforms-success
                         :value   (or slack-transforms-success false)
                         :process identity}
                        {:name    :slack-transforms-failure
                         :value   (or slack-transforms-failure false)
                         :process identity}
                        {:name    :slack-extracts-failure
                         :value   (or slack-extracts-failure false)
                         :process identity}
                        {:name    :slack-extracts-success
                         :value   (or slack-extracts-success false)
                         :process identity}]})
          (fn [fields errors on-submit]
            [notification-preferences-slack-form-render
             {:on-submit       on-submit
              :errors          errors
              :fields          fields

              :list-loading?   @list-loading?
              :action-loading? action-loading?}])]

         [form-component/form
          (->form-model
           {:id        :notification-preferences-teams-form
            :on-submit submit-notification-preferences-form
            :fields    [{:name  :ms-teams-webhook
                         :value (or ms-teams-webhook "")}
                        {:name    :ms-teams-webhook-workflows
                         :value   (or ms-teams-webhook-workflows false)
                         :process identity}
                        {:name    :ms-teams-transforms-success
                         :value   (or ms-teams-transforms-success false)
                         :process identity}
                        {:name    :ms-teams-transforms-failure
                         :value   (or ms-teams-transforms-failure false)
                         :process identity}
                        {:name    :ms-teams-extracts-failure
                         :value   (or ms-teams-extracts-failure false)
                         :process identity}
                        {:name    :ms-teams-extracts-success
                         :value   (or ms-teams-extracts-success false)
                         :process identity}]})
          (fn [fields errors on-submit]
            [notification-preferences-teams-form-render
             {:on-submit       on-submit
              :errors          errors
              :fields          fields
              :list-loading?   @list-loading?
              :action-loading? action-loading?}])]

         [form-component/form
          (->form-model
           {:id        :notification-preferences-generic-form
            :on-submit submit-notification-preferences-form
            :fields    [{:name  :generic-webhook
                         :value (or generic-webhook "")}
                        {:name    :generic-transforms-success
                         :value   (or generic-transforms-success false)
                         :process identity}
                        {:name    :generic-transforms-failure
                         :value   (or generic-transforms-failure false)
                         :process identity}
                        {:name    :generic-extracts-failure
                         :value   (or generic-extracts-failure false)
                         :process identity}
                        {:name    :generic-extracts-success
                         :value   (or generic-extracts-success false)
                         :process identity}]})
          (fn [fields errors on-submit]
            [notification-preferences-generic-form-render
             {:on-submit       on-submit
              :errors          errors
              :fields          fields
              :list-loading?   @list-loading?
              :action-loading? action-loading?}])]]))}))


(defn job-notification-preference-item
  [{:notification/keys [preference-id job-name source-type source
                        target-type target use-account-slack use-account-ms-teams]}]
  (let [original-value (reagent/atom target)
        current-value  (reagent/atom target)]
    (fn []
      [card {:class "mb-4"}
       [card-content
        [:div {:class "flex flex-col gap-2"}
         [:div {:class "flex items-center justify-between"}
          [:div {:class "flex items-center gap-2"}
           [:span {:class "text-kleene-text-secondary"} source-type]
           [typography/h4 job-name]]
          [:span {:class "text-kleene-text-secondary"} target-type]]

         [:div {:class "flex items-end gap-2"}
          (if (or use-account-slack use-account-ms-teams)
            [typography/h3 {:class "flex-grow pr-4 text-kleene-secondary-02"}
             "Will use the Webhook URL from general notifications preferences settings section"]
            [input {:value     @current-value
                    :id        (str "target-" job-name)
                    :variant   "standard"
                    :class     "flex-grow pr-4"
                    :label     (if (= target-type "email") "Email address" "Webhook URL")
                    :on-change #(reset! current-value (jsi/get-in % [:target :value]))}])

          (when (not= @original-value @current-value)
            [button {:class    "text-kleene-text-success shrink-0 !p-2"
                     :variant  "outlined"
                     :on-click #(do
                                  (dispatch [:users.api/update-notification-preference
                                             {:preference-id preference-id
                                              :source        source
                                              :source-type   source-type
                                              :target        @current-value
                                              :target-type   target-type
                                              :status        "transition"}])
                                  (reset! original-value @current-value))}
             [:> Save {:size 16}]])

          [button {:variant  "outlined"
                   :class    "text-kleene-text-error border-kleene-text-error shrink-0 !p-2"
                   :on-click #(dispatch [:users.api/delete-notification-preference
                                         {:preference-id preference-id}])}
           [:> Trash2 {:size 16}]]]]]])))


(defn jobs-notification-preferences-form []
  (reagent/create-class
   {:display-name "jobs-notification-preferences"

    :constructor
    (fn []
      (dispatch [:notifications.api/fetch-all-jobs-notification]))

    :render
    (fn []
      (let [notifications-loading? (re-posh/subscribe [:mold-http/loading? :notifications.api/fetch-all-jobs-notification])
            jobs-notifications     (re-posh/subscribe [:notification/pull-many])]
        [:div {:class "flex flex-col gap-8 max-w-3xl"}
         [:header
          [card-title "Notification preferences for individual jobs"]]

         [:div
          (cond
            @notifications-loading?
            [spinner.core/spinner-lucide {:size 24 :class "mx-auto mb-4"}]

            (empty? @jobs-notifications)
            [typography/p "No notification preferences found."]

            :otherwise
            (for [preference @jobs-notifications]
              ^{:key (:notification/preference-id preference)}
              [job-notification-preference-item preference]))]]))}))
